import {Box, Stack} from "@mui/material";
import Typography from "@mui/material/Typography";
import {SurveyQuestion} from "crqlar-react-common/dist/models/survey/surveyQuestion.model";
import {SurveyRedactionMark} from "crqlar-react-common/dist/models/survey/surveyRedaction.model";
import {SurveyAnswer} from "crqlar-react-common/dist/models/survey/surveySubmission.model";
import {formatDateDMYHM} from "crqlar-react-common/dist/utils/date.util";
import {useLocalize} from "crqlar-react-common/dist/utils/text.util";
import React from "react";
import {
    SURVEY_ANSWER_PART_SEPARATOR,
    SurveyAnswerContext,
    SurveyAnswerValue,
    surveyAnswerParts
} from "../../shared/surveyAnswer.util";
import {SurveyAnswerMedia} from "../../shared/SurveyAnswerMedia";
import {buildSurveyRedactionSegments} from "./surveyRedactionSegments";

interface SurveyRedactedAnswerValueProps {
    answer: SurveyAnswer | undefined
    question: SurveyQuestion | undefined
    ctx: SurveyAnswerContext
    marks?: SurveyRedactionMark[]
}

/**
 * The read-only bar, and deliberately NOT `SurveyBlackoutBar`.
 *
 * What it covers is the mask the server already wrote — asterisks, of a fixed width that says
 * nothing about the original. The editor's bar covers the real characters and therefore has to
 * match the native selection highlight to the pixel; this one has no such duty and only has to sit
 * inline in a one-line, ellipsised answer value. Same look, unrelated constraints: merging them
 * would tie a list cell's styling to the editor's selection geometry.
 */
const RedactionBar = ({text, label}: {text: string, label: string}) => (
    <Box
        component={'span'}
        aria-label={label}
        sx={{
            bgcolor: 'text.primary',
            color: 'text.primary',
            borderRadius: '3px',
            userSelect: 'none',
        }}
    >
        {text}
    </Box>
);

/**
 * A masked string with its redaction bars drawn inline: the string-level piece of the renderer
 * below, for table cells that preview answer text outside a full answer value. `marks` must
 * already be filtered to the question and field the text belongs to.
 */
export function SurveyRedactedTextRuns({text, marks, leadingTrim = 0}: {
    text: string
    marks: SurveyRedactionMark[]
    leadingTrim?: number
}) {
    const {t} = useLocalize();

    if (!marks.length) return <>{text}</>;

    return (
        <>
            {buildSurveyRedactionSegments(text, marks, leadingTrim).map((segment, segmentIndex) => (
                segment.redacted
                    ? <RedactionBar key={segmentIndex} text={segment.text} label={t('Redacted')}/>
                    : <React.Fragment key={segmentIndex}>{segment.text}</React.Fragment>
            ))}
        </>
    );
}

/**
 * An answer value with its redactions drawn in: the masked side's renderer, used everywhere staff
 * read a submission normally. Without marks it is exactly `SurveyAnswerValue`.
 */
function SurveyRedactedAnswerValue({
    answer,
    question,
    ctx,
    marks
}: SurveyRedactedAnswerValueProps) {
    const {t} = useLocalize();

    if (!marks?.length) {
        return <SurveyAnswerValue answer={answer} question={question} ctx={ctx}/>;
    }

    const latestMark = marks.reduce((latest, mark) => (
        (mark.redactedAt?.valueOf() ?? 0) > (latest.redactedAt?.valueOf() ?? 0) ? mark : latest
    ), marks[0]);

    const caption = (
        <Typography
            variant={'body2'}
            color={'text.secondary'}
            sx={{display: 'block', mt: 0.5}}
        >
            {t('Blacked out by {{name}} · {{date}}', {
                name: latestMark.redactedByName || t('Unknown'),
                date: formatDateDMYHM(latestMark.redactedAt),
            })}
        </Typography>
    );

    const {parts} = surveyAnswerParts(answer, question, ctx);

    return (
        <>
            <Stack gap={1.5} sx={{minWidth: 0}}>
                {!!parts.length && (
                    <Box sx={{minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis'}}>
                        {parts.map((part, partIndex) => {
                            const partMarks = part.field
                                ? marks.filter(mark => mark.field === part.field)
                                : [];

                            return (
                                <React.Fragment key={partIndex}>
                                    {partIndex > 0 && SURVEY_ANSWER_PART_SEPARATOR}

                                    <SurveyRedactedTextRuns
                                        text={part.text}
                                        marks={partMarks}
                                        leadingTrim={part.leadingTrim}
                                    />
                                </React.Fragment>
                            );
                        })}
                    </Box>
                )}

                <SurveyAnswerMedia answer={answer} ctx={ctx}/>
            </Stack>

            {caption}
        </>
    );
}

export default SurveyRedactedAnswerValue;
